import { useEffect, useState, useMemo, useCallback } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Clock, User, Scissors, CheckCircle2, Banknote, Smartphone, CreditCard, ArrowRightLeft, FileText, GripVertical } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import VoiceInputButton from "@/components/VoiceInputButton";
import AgendaColumnReorder from "@/components/AgendaColumnReorder";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "@/styles/calendar.css";

const locales = { "pt-BR": ptBR };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

const STATUS_LABELS = {
  agendado: { label: "Agendado", color: "#3B82F6" },        // azul - aguardando
  confirmado: { label: "Confirmado", color: "#CBA871" },     // dourado - confirmado
  atendendo: { label: "Em Atendimento", color: "#7C3AED" },  // roxo - em andamento
  concluido: { label: "Finalizado", color: "#166534" },      // verde - finalizado
  nao_compareceu: { label: "Não Compareceu", color: "#DC2626" }, // vermelho - faltou
  remarcado: { label: "Remarcado", color: "#F59E0B" },       // âmbar - remarcado
  cancelado: { label: "Cancelado", color: "#737373" },       // cinza - cancelado
};

const messages = {
  date: "Data",
  time: "Hora",
  event: "Evento",
  allDay: "Dia todo",
  week: "Semana",
  work_week: "Semana de trabalho",
  day: "Dia",
  month: "Mês",
  previous: "Anterior",
  next: "Próximo",
  yesterday: "Ontem",
  tomorrow: "Amanhã",
  today: "Hoje",
  agenda: "Lista",
  noEventsInRange: "Nenhum agendamento neste período.",
  showMore: (total) => `+${total} mais`,
};

export default function Agenda() {
  const { apiCall, user } = useAuth();
  const searchParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const initialDateParam = searchParams.get("date");
  const focusAppointmentId = searchParams.get("focus");
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeForm, setCompleteForm] = useState({ tip_amount: "0", notes: "" });
  const [paymentSplits, setPaymentSplits] = useState([{ payment_method: "dinheiro", amount: "" }]);
  const [clientCreditBalance, setClientCreditBalance] = useState(0);
  const [sameDaySiblings, setSameDaySiblings] = useState([]);
  const [combineIds, setCombineIds] = useState([]);
  const [creditDeposit, setCreditDeposit] = useState("0");
  const [serviceSearch, setServiceSearch] = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({ date: "", time: "", employee_id: "", notes: "" });
  const [priceOverrideEnabled, setPriceOverrideEnabled] = useState(false);
  const [priceOverride, setPriceOverride] = useState("");
  const [priceOverrideReason, setPriceOverrideReason] = useState("");
  const [agendaStatusFilter, setAgendaStatusFilter] = useState(null);
  const [extraItems, setExtraItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [extraServiceSearch, setExtraServiceSearch] = useState("");
  const [extraProductSearch, setExtraProductSearch] = useState("");
  const [view, setView] = useState("day");
  const [date, setDate] = useState(() => {
    if (initialDateParam) {
      const [y, m, d] = initialDateParam.split("-").map(Number);
      if (y && m && d) return new Date(y, m - 1, d);
    }
    return new Date();
  });
  const [clientSearch, setClientSearch] = useState("");
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ name: "", phone: "", email: "" });
  const [showReorder, setShowReorder] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    employee_id: "",
    service_ids: [],
    date: "",
    time: "",
    notes: "",
    custom_price: "",
  });

  // Auto-abre o dialog de novo agendamento se a URL pedir (?novo=1 — atalho PWA)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("novo") === "1") {
      const now = new Date();
      setForm({
        client_id: "",
        employee_id: "",
        service_ids: [],
        date: now.toISOString().slice(0, 10),
        time: now.toTimeString().slice(0, 5),
        notes: "",
        custom_price: "",
      });
      setOpen(true);
      params.delete("novo");
      const newUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, cRes, sRes, eRes, pRes] = await Promise.all([
        apiCall("get", "/appointments"),
        apiCall("get", "/clients"),
        apiCall("get", "/services"),
        apiCall("get", "/employees"),
        apiCall("get", "/products").catch(() => ({ data: [] })),
      ]);
      setAppointments(aRes.data);
      setClients(cRes.data);
      setServices(sRes.data);
      setEmployees(eRes.data.filter((e) => e.active !== false && e.participates_in_agenda !== false));
      setProducts(pRes.data || []);
    } catch {
      toast.error("Erro ao carregar agenda");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Open appointment details when navigated with ?focus=appointment_id
  // Se `?action=finalize`, abre direto o dialog de Finalizar Atendimento
  useEffect(() => {
    if (!focusAppointmentId || appointments.length === 0) return;
    const found = appointments.find((a) => a.appointment_id === focusAppointmentId);
    if (found) {
      const action = searchParams.get("action");
      if (action === "finalize" && found.status !== "concluido") {
        openCompleteFlow(found);
      } else {
        setSelectedApp(found);
        setDetailsOpen(true);
      }
      const params = new URLSearchParams(window.location.search);
      params.delete("focus");
      params.delete("action");
      const newUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusAppointmentId, appointments]);

  // Auto-sync the single payment split amount when extras change in the complete dialog
  useEffect(() => {
    if (!completeOpen) return;
    if (paymentSplits.length !== 1) return; // só auto-ajusta se for split único
    const total = computeCombinedTotal();
    const current = parseFloat(paymentSplits[0]?.amount) || 0;
    if (Math.abs(total - current) > 0.001) {
      setPaymentSplits([{ payment_method: paymentSplits[0]?.payment_method || "dinheiro", amount: total.toFixed(2) }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraItems, completeOpen]);


  const events = useMemo(
    () =>
      appointments
        .filter((a) => !agendaStatusFilter || a.status === agendaStatusFilter)
        .map((a) => {
        const start = new Date(a.date);
        const end = new Date(start.getTime() + (a.duration_minutes || 30) * 60000);
        const firstName = (a.client_name || "").split(/\s+/)[0] || a.client_name;
        const firstService = (a.services || [])[0]?.service_name || (a.services || [])[0]?.name || "";
        return {
          id: a.appointment_id,
          title: firstService ? `${firstName} · ${firstService}` : firstName,
          start,
          end,
          resource: a,
          resourceId: a.employee_id || "_unassigned",
        };
      }),
    [appointments, agendaStatusFilter]
  );

  // Resources (colunas por funcionário na visão dia)
  // Ordem persistida das colunas (drag-and-drop manual) — escopo por usuário
  const orderKey = `agenda_column_order_${user?.user_id || "anon"}`;
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(orderKey) || "[]");
    } catch {
      return [];
    }
  });

  const saveColumnOrder = useCallback((newOrder) => {
    setColumnOrder(newOrder);
    localStorage.setItem(orderKey, JSON.stringify(newOrder));
  }, [orderKey]);

  const resources = useMemo(() => {
    const activeEmps = (employees || [])
      .filter((e) => e.active !== false)
      .map((e) => ({ resourceId: e.employee_id, resourceTitle: e.name }));
    // Ordena conforme columnOrder (IDs salvos)
    const byId = new Map(activeEmps.map((r) => [r.resourceId, r]));
    const ordered = [];
    for (const id of columnOrder) {
      if (byId.has(id)) {
        ordered.push(byId.get(id));
        byId.delete(id);
      }
    }
    // Adiciona os funcionários novos (não ainda na ordem salva) no final
    for (const r of byId.values()) ordered.push(r);
    const hasUnassigned = appointments.some((a) => !a.employee_id);
    if (hasUnassigned) ordered.push({ resourceId: "_unassigned", resourceTitle: "Sem profissional" });
    return ordered;
  }, [employees, appointments, columnOrder]);

  // Set de clientes com 2+ agendamentos NO MESMO DIA (para destacar visualmente)
  const multiApptClientByDate = useMemo(() => {
    const m = new Map(); // key: `${client_id}|${YYYY-MM-DD}` -> count
    for (const a of appointments) {
      const day = new Date(a.date).toISOString().slice(0, 10);
      const k = `${a.client_id}|${day}`;
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [appointments]);

  // Map de clientes para tooltip com notas
  const clientsMap = useMemo(() => {
    const m = {};
    for (const c of clients) m[c.client_id] = c;
    return m;
  }, [clients]);

  // Event component com tooltip de notas do cliente
  const EventComponent = ({ event }) => {
    const app = event.resource;
    const client = clientsMap[app.client_id];
    const notes = client?.notes;
    const tags = (client?.tags || []).join(", ");
    const day = new Date(app.date).toISOString().slice(0, 10);
    const multiCount = multiApptClientByDate.get(`${app.client_id}|${day}`) || 1;
    const isMulti = multiCount > 1;
    const tooltipParts = [
      `${app.client_name}`,
      app.employee_name ? `Profissional: ${app.employee_name}` : null,
      (app.services || []).length > 0 ? `Serviços: ${(app.services || []).map((s) => s.service_name || s.name).join(", ")}` : null,
      app.total_price ? `R$ ${Number(app.total_price).toFixed(2)}` : null,
      tags ? `Tags: ${tags}` : null,
      notes ? `📝 ${notes}` : null,
      isMulti ? `🔗 Cliente tem ${multiCount} atendimentos hoje` : null,
    ].filter(Boolean);
    return (
      <div title={tooltipParts.join("\n")} className="leading-tight">
        <div className="flex items-center gap-1 text-[11px] font-medium">
          {isMulti && (
            <span
              className="inline-flex items-center justify-center text-[9px] font-bold rounded-full px-1 min-w-[14px] h-[14px]"
              style={{ background: "#FBBF24", color: "#000" }}
              title={`Cliente tem ${multiCount} atendimentos hoje`}
            >
              ×{multiCount}
            </span>
          )}
          {notes && <span title="Tem anotações" className="text-yellow-300">★</span>}
          {event.title}
        </div>
      </div>
    );
  };

  const eventStyleGetter = (event) => {
    const app = event.resource;
    const status = app.status;
    const bgColor = STATUS_LABELS[status]?.color || "#737373";
    const day = new Date(app.date).toISOString().slice(0, 10);
    const multiCount = multiApptClientByDate.get(`${app.client_id}|${day}`) || 1;
    const isMulti = multiCount > 1;
    return {
      style: {
        backgroundColor: bgColor,
        color: "#fff",
        border: isMulti ? "2px solid #FBBF24" : "none",
        boxShadow: isMulti ? "0 0 0 1px #FBBF24 inset" : "none",
        borderRadius: "4px",
        fontSize: "12px",
        padding: "2px 4px",
      },
    };
  };

  const handleSelectSlot = ({ start, resourceId }) => {
    const d = new Date(start);
    // Use LOCAL date components to avoid UTC offset shifting the day/hour
    // (e.g., clicking 22:00 BRT was becoming 04/06 in UTC instead of 03/06).
    const pad = (n) => String(n).padStart(2, "0");
    const localDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const localTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setForm({
      client_id: "",
      employee_id: resourceId && resourceId !== "_unassigned" ? resourceId : "",
      service_ids: [],
      date: localDate,
      time: localTime,
      notes: "",
      custom_price: "",
    });
    setOpen(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedApp(event.resource);
    setDetailsOpen(true);
  };

  const handleDoubleClickEvent = (event) => {
    // Double-click: jump straight to payment/completion flow
    const app = event.resource;
    if (app.status === "concluido") {
      setSelectedApp(app);
      setDetailsOpen(true);
      return;
    }
    openCompleteFlow(app);
  };

  const openCompleteFlow = async (app) => {
    setSelectedApp(app);
    setCompleteForm({ tip_amount: "0", notes: "" });
    setPaymentSplits([{ payment_method: "dinheiro", amount: (app.total_price || 0).toFixed(2) }]);
    setClientCreditBalance(0);
    setCreditDeposit("0");
    setCombineIds([]);
    setSameDaySiblings([]);
    setPriceOverrideEnabled(false);
    setPriceOverride("");
    setPriceOverrideReason("");
    setExtraItems([]);
    setExtraServiceSearch("");
    setExtraProductSearch("");
    if (app.client_id) {
      try {
        const cRes = await apiCall("get", `/clients/${app.client_id}`);
        setClientCreditBalance(parseFloat(cRes.data?.credit_balance || 0));
      } catch { /* ignore */ }
    }
    try {
      const sRes = await apiCall("get", `/appointments/${app.appointment_id}/same-day-siblings`);
      setSameDaySiblings(sRes.data || []);
    } catch { /* ignore */ }
    setCompleteOpen(true);
  };

  // Total dynamic: (override OR base) + siblings selected + extra items
  const computeCombinedTotal = () => {
    const base = priceOverrideEnabled
      ? (parseFloat(priceOverride) || 0)
      : parseFloat(selectedApp?.total_price || 0);
    const extra = sameDaySiblings
      .filter((s) => combineIds.includes(s.appointment_id))
      .reduce((a, b) => a + parseFloat(b.total_price || 0), 0);
    const extras = extraItems.reduce((a, b) => a + (parseFloat(b.unit_price) || 0) * (parseFloat(b.quantity) || 0), 0);
    return base + extra + extras;
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    const total = computeCombinedTotal();
    const deposit = parseFloat(creditDeposit) || 0;
    const expected = total + deposit;
    const cleanedSplits = paymentSplits
      .map((s) => ({ payment_method: s.payment_method, amount: parseFloat(s.amount) || 0 }))
      .filter((s) => s.amount > 0);
    const sum = cleanedSplits.reduce((a, b) => a + b.amount, 0);
    if (cleanedSplits.length === 0) {
      toast.error("Adicione ao menos uma forma de pagamento");
      return;
    }
    if (Math.abs(sum - expected) > 0.02) {
      const diff = expected - sum;
      toast.error(
        diff > 0
          ? `Faltam R$ ${diff.toFixed(2)} (Comanda R$ ${total.toFixed(2)}${deposit > 0 ? ` + crédito R$ ${deposit.toFixed(2)}` : ""}).`
          : `Pagamento excede em R$ ${(-diff).toFixed(2)}. Marque "Deixar como crédito" ou ajuste valores.`
      );
      return;
    }
    if (priceOverrideEnabled && !priceOverrideReason.trim()) {
      toast.error("Justificativa obrigatória para alteração de valor");
      return;
    }
    try {
      const overrideNote = priceOverrideEnabled
        ? `Valor alterado de R$ ${parseFloat(selectedApp.total_price).toFixed(2)} para R$ ${parseFloat(priceOverride).toFixed(2)} · ${priceOverrideReason}`
        : null;
      await apiCall("post", `/appointments/${selectedApp.appointment_id}/complete`, {
        splits: cleanedSplits,
        tip_amount: parseFloat(completeForm.tip_amount) || 0,
        credit_deposit: deposit,
        additional_appointment_ids: combineIds,
        price_override: priceOverrideEnabled ? parseFloat(priceOverride) : null,
        extra_items: extraItems
          .filter((x) => x.name && (parseFloat(x.unit_price) || 0) > 0 && (parseFloat(x.quantity) || 0) > 0)
          .map((x) => ({
            type: x.type,
            name: x.name,
            quantity: parseFloat(x.quantity) || 1,
            unit_price: parseFloat(x.unit_price) || 0,
            item_id: x.item_id || null,
            employee_id: x.employee_id || null,
            employee_name: x.employee_name || null,
            commission_rate: x.commission_rate != null ? Number(x.commission_rate) : null,
          })),
        notes: [overrideNote, completeForm.notes].filter(Boolean).join(" · ") || null,
      });
      const hasFiado = cleanedSplits.some((s) => s.payment_method === "fiado");
      const nCombined = combineIds.length;
      toast.success(
        nCombined > 0
          ? `${nCombined + 1} comandas finalizadas juntas${hasFiado ? " · Fiado registrado" : ""}${deposit > 0 ? ` · R$ ${deposit.toFixed(2)} de crédito` : ""}`
          : hasFiado
            ? "Comanda finalizada · Fiado registrado"
            : "Comanda finalizada e pagamento registrado!"
      );
      setCompleteOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // iter67: bloqueio por caixa fechado — mostra aviso claro com CTA
      if (err.response?.status === 409 && detail && typeof detail === "object" && detail.reason === "no_open_cash_session") {
        toast.error(
          "Caixa fechado — abra o caixa em Financeiro antes de finalizar comandas.",
          { duration: 6000 }
        );
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Erro ao finalizar");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Selecione um cliente");
      return;
    }
    if (form.service_ids.length === 0) {
      toast.error("Selecione ao menos um serviço");
      return;
    }
    try {
      const dateTime = new Date(`${form.date}T${form.time}`).toISOString();
      await apiCall("post", "/appointments", {
        client_id: form.client_id,
        employee_id: form.employee_id || null,
        service_ids: form.service_ids,
        date: dateTime,
        notes: form.notes,
        custom_price: form.custom_price !== "" && form.custom_price !== undefined && form.custom_price !== null
          ? parseFloat(form.custom_price)
          : null,
      });
      toast.success("Agendamento criado");
      setOpen(false);
      setForm({ client_id: "", employee_id: "", service_ids: [], date: "", time: "", notes: "", custom_price: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao agendar");
    }
  };

  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    if (!rescheduleForm.date || !rescheduleForm.time) {
      toast.error("Informe data e horário");
      return;
    }
    try {
      const dateTime = new Date(`${rescheduleForm.date}T${rescheduleForm.time}`).toISOString();
      await apiCall("put", `/appointments/${selectedApp.appointment_id}/reschedule`, {
        date: dateTime,
        employee_id: rescheduleForm.employee_id || null,
        notes: rescheduleForm.notes || null,
      });
      toast.success("Agendamento remarcado · registrado no histórico");
      setRescheduleOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao reagendar");
    }
  };

  const handleCreateNewClient = async (e) => {
    e.preventDefault();
    if (!newClientForm.name.trim()) {
      toast.error("Nome do cliente é obrigatório");
      return;
    }
    try {
      const res = await apiCall("post", "/clients", {
        name: newClientForm.name.trim(),
        phone: newClientForm.phone.trim() || "—",
        email: newClientForm.email.trim() || null,
      });
      const created = res.data;
      // Refresh client list and auto-select the new one
      const cRes = await apiCall("get", "/clients");
      setClients(cRes.data);
      setForm((f) => ({ ...f, client_id: created.client_id }));
      setNewClientForm({ name: "", phone: "", email: "" });
      setNewClientOpen(false);
      setClientSearch("");
      toast.success(`Cliente "${created.name}" criado e selecionado`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar cliente");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await apiCall("put", `/appointments/${id}?status=${status}`);
      toast.success("Status atualizado");
      load();
      setDetailsOpen(false);
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Excluir este agendamento?")) return;
    try {
      await apiCall("delete", `/appointments/${id}`);
      toast.success("Excluído");
      load();
      setDetailsOpen(false);
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      service_ids: f.service_ids.includes(id) ? f.service_ids.filter((x) => x !== id) : [...f.service_ids, id],
    }));
  };

  const selectedServicesData = services.filter((s) => form.service_ids.includes(s.service_id));
  const totalDuration = selectedServicesData.reduce((sum, s) => sum + s.duration_minutes, 0);
  const totalPrice = selectedServicesData.reduce((sum, s) => sum + s.price, 0);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);

  return (
    <div className="space-y-2" data-testid="agenda-page">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-base font-medium tracking-tight">Agenda</h1>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Date picker — go to any date with one click */}
          <Input
            type="date"
            value={date.toISOString().slice(0, 10)}
            onChange={(e) => {
              if (!e.target.value) return;
              const [y, m, d] = e.target.value.split("-").map(Number);
              setDate(new Date(y, m - 1, d));
              setView("day");
            }}
            className="h-7 w-[130px] text-xs"
            data-testid="agenda-date-picker"
          />
          {view === "day" && resources.length > 1 && (
            <Button
              size="sm"
              variant={showReorder ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={() => setShowReorder((v) => !v)}
              data-testid="agenda-toggle-reorder"
              title="Organizar colunas dos funcionários"
            >
              <GripVertical className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Button>
          )}
          <Button
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={() => {
              const now = new Date();
              setForm({
                client_id: "",
                employee_id: "",
                service_ids: [],
                date: now.toISOString().slice(0, 10),
                time: now.toTimeString().slice(0, 5),
                notes: "",
                custom_price: "",
              });
              setOpen(true);
            }}
            data-testid="add-appointment-button"
            disabled={services.length === 0}
          >
            <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
            Novo
          </Button>
        </div>
      </div>

      {/* Legend — clickable to filter by status */}
      <div className="flex items-center gap-2 flex-wrap">
        {agendaStatusFilter && (
          <button
            type="button"
            onClick={() => setAgendaStatusFilter(null)}
            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-foreground text-background"
            data-testid="legend-clear"
          >
            ✕ Limpar filtro
          </button>
        )}
        {Object.entries(STATUS_LABELS).map(([key, val]) => {
          const active = agendaStatusFilter === key;
          return (
            <button
              type="button"
              key={key}
              onClick={() => setAgendaStatusFilter(active ? null : key)}
              className={`flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm border transition-all cursor-pointer ${active ? "border-foreground bg-secondary" : "border-transparent hover:border-border"}`}
              data-testid={`legend-${key}`}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-sm" style={{ background: val.color }} />
              <span className="text-muted-foreground uppercase tracking-wider">{val.label}</span>
            </button>
          );
        })}
      </div>

      {(clients.length === 0 || services.length === 0) && (
        <Card className="border border-border bg-card p-3 shadow-soft rounded-md">
          <p className="text-xs text-muted-foreground">Cadastre clientes e serviços primeiro para criar agendamentos.</p>
        </Card>
      )}

      <Card className="border border-border bg-card p-1.5 shadow-soft rounded-md agenda-compact">
        {view === "day" && resources.length > 1 && showReorder && (
          <AgendaColumnReorder
            items={resources.filter((r) => r.resourceId !== "_unassigned")}
            onChange={saveColumnOrder}
          />
        )}
        <div className="h-[calc(100vh-130px)] min-h-[520px]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">Carregando agenda...</div>
          ) : (
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              culture="pt-BR"
              messages={messages}
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              onDrillDown={(d) => { setDate(d); setView("day"); }}
              views={["month", "week", "day", "agenda"]}
              selectable
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onDoubleClickEvent={handleDoubleClickEvent}
              eventPropGetter={eventStyleGetter}
              dayPropGetter={(d) => {
                // Salão fecha domingo (0) e segunda (1)
                const dow = d.getDay();
                if (dow === 0 || dow === 1) {
                  return {
                    className: "rbc-closed-day",
                    style: { backgroundColor: "hsl(var(--muted))", opacity: 0.4 },
                  };
                }
                return {};
              }}
              step={30}
              timeslots={1}
              min={new Date(2020, 0, 1, 9, 0)}
              max={new Date(2020, 0, 1, 20, 0)}
              resources={view === "day" ? resources : undefined}
              resourceIdAccessor="resourceId"
              resourceTitleAccessor="resourceTitle"
              components={{ event: EventComponent }}
            />
          )}
        </div>
      </Card>

      {/* New Appointment Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4">
          <DialogHeader className="pb-1">
            <DialogTitle className="font-light tracking-tight text-lg">Novo Agendamento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-2.5 mt-1">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-widest">Cliente</Label>
                <button
                  type="button"
                  onClick={() => setNewClientOpen(true)}
                  className="text-xs underline text-primary hover:opacity-80"
                  data-testid="appointment-new-client-button"
                >
                  + Novo cliente
                </button>
              </div>
              <Input
                placeholder={form.client_id
                  ? clients.find((c) => c.client_id === form.client_id)?.name || "Buscar cliente..."
                  : "Buscar cliente por nome ou telefone..."}
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                data-testid="appointment-client-search"
                className="mb-2"
              />
              <div className="border border-border rounded-md max-h-32 overflow-y-auto bg-background">
                {(() => {
                  const q = clientSearch.trim().toLowerCase();
                  const filtered = q
                    ? clients.filter(
                        (c) =>
                          (c.name || "").toLowerCase().includes(q) ||
                          (c.phone || "").toLowerCase().includes(q)
                      ).slice(0, 50)
                    : clients.slice(0, 50);
                  if (clients.length === 0) {
                    return <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum cliente cadastrado. Clique em "+ Novo cliente".</div>;
                  }
                  if (filtered.length === 0) {
                    return <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum cliente encontrado para "{clientSearch}".</div>;
                  }
                  return filtered.map((c) => (
                    <button
                      type="button"
                      key={c.client_id}
                      onClick={() => { setForm({ ...form, client_id: c.client_id }); setClientSearch(""); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-accent border-b border-border/40 last:border-b-0 ${form.client_id === c.client_id ? "bg-accent" : ""}`}
                      data-testid={`appointment-client-option-${c.client_id}`}
                    >
                      <div className="font-medium">{c.name}</div>
                      {c.phone && <div className="text-[10px] text-muted-foreground">{c.phone}</div>}
                    </button>
                  ));
                })()}
              </div>
              {!form.client_id && (
                <div className="text-[10px] text-destructive mt-1">Selecione um cliente da lista</div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest">Funcionário</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="appointment-employee-select"><SelectValue placeholder="Sem funcionário específico" /></SelectTrigger>
                <SelectContent>
                  {employees.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-muted-foreground">Cadastre funcionários primeiro</div>
                  ) : (
                    employees.map((e) => (
                      <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`appointment-employee-option-${e.employee_id}`}>
                        {e.name} {e.role ? `(${e.role})` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-widest">Serviços (selecione um ou mais)</Label>
                {form.service_ids.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    {form.service_ids.length} selecionado{form.service_ids.length > 1 ? "s" : ""} · {totalDuration} min · {formatCurrency(totalPrice)}
                  </span>
                )}
              </div>
              <Input
                placeholder="Buscar serviço..."
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
                className="h-8 mb-1.5"
                data-testid="appointment-service-search"
              />
              <div className="border border-border rounded-md max-h-44 overflow-y-auto">
                {(() => {
                  const q = serviceSearch.trim().toLowerCase();
                  const filtered = q
                    ? services.filter((s) => (s.name || "").toLowerCase().includes(q))
                    : services;
                  if (filtered.length === 0) {
                    return <div className="px-3 py-3 text-xs text-muted-foreground">Nenhum serviço encontrado.</div>;
                  }
                  return filtered.map((s) => (
                    <label
                      key={s.service_id}
                      className="flex items-center gap-2 px-3 py-2 border-b border-border/40 last:border-b-0 cursor-pointer hover:bg-secondary/60 transition-colors"
                      data-testid={`appointment-service-option-${s.service_id}`}
                    >
                      <Checkbox
                        checked={form.service_ids.includes(s.service_id)}
                        onCheckedChange={() => toggleService(s.service_id)}
                      />
                      <div className="flex-1 flex items-center justify-between min-w-0">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{s.name}</div>
                          <div className="text-[10px] text-muted-foreground">{s.duration_minutes} min</div>
                        </div>
                        <div className="text-xs font-medium ml-2" style={{ color: "#CBA871" }}>{formatCurrency(s.price)}</div>
                      </div>
                    </label>
                  ));
                })()}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="appointment-date-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Hora</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required data-testid="appointment-time-input" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest">
                Valor do orçamento (R$) <span className="text-muted-foreground normal-case font-normal">— deixe em branco para usar o padrão de {formatCurrency(totalPrice)}</span>
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder={totalPrice ? totalPrice.toFixed(2) : "0,00"}
                value={form.custom_price}
                onChange={(e) => setForm({ ...form, custom_price: e.target.value })}
                data-testid="appointment-custom-price-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
                Observações
                <VoiceInputButton
                  size="sm"
                  testId="appointment-notes-voice"
                  onTranscript={(text) => setForm((f) => ({ ...f, notes: text }))}
                />
              </Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="appointment-notes-input" />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="appointment-save-button">Agendar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Appointment Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Detalhes do Agendamento</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-sm">{new Date(selectedApp.date).toLocaleString("pt-BR")}</span>
              </div>
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-sm font-medium">{selectedApp.client_name}</span>
              </div>
              {selectedApp.employee_name && (
                <div className="flex items-center gap-3">
                  <Scissors className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="text-sm">{selectedApp.employee_name}</span>
                </div>
              )}
              <div className="pt-3 border-t border-border">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Serviços</div>
                <div className="space-y-1">
                  {selectedApp.services?.map((s, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{s.service_name}</span>
                      <span className="text-muted-foreground">{formatCurrency(s.price)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm pt-2 border-t border-border font-medium">
                    <span>Total</span>
                    <span style={{ color: "#CBA871" }}>{formatCurrency(selectedApp.total_price)}</span>
                  </div>
                </div>
              </div>
              {selectedApp.extra_items && selectedApp.extra_items.length > 0 && (
                <div className="pt-3 border-t border-border" data-testid="appointment-details-extras">
                  <div className="text-xs uppercase tracking-widest text-amber-700 mb-2">
                    Extras vendidos no fechamento
                  </div>
                  <div className="space-y-1">
                    {selectedApp.extra_items.map((ex, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>
                          <span className="text-[10px] uppercase tracking-wider text-amber-700 mr-1">
                            {ex.type === "produto" ? "Prod" : "Serv"}
                          </span>
                          {ex.name}
                          {ex.quantity > 1 && <span className="text-muted-foreground"> · {ex.quantity}x</span>}
                          {ex.employee_name && <span className="text-muted-foreground"> · {ex.employee_name}</span>}
                        </span>
                        <span className="text-muted-foreground">{formatCurrency(ex.total ?? ex.unit_price * ex.quantity)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs pt-1 border-t border-border font-medium text-amber-800">
                      <span>Subtotal extras</span>
                      <span>{formatCurrency(selectedApp.extras_total || selectedApp.extra_items.reduce((a, b) => a + (b.total ?? (b.unit_price || 0) * (b.quantity || 1)), 0))}</span>
                    </div>
                  </div>
                </div>
              )}
              {selectedApp.notes && (
                <div className="pt-3 border-t border-border">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Observações</div>
                  <p className="text-sm">{selectedApp.notes}</p>
                </div>
              )}
              <div className="pt-3 border-t border-border">
                <Label className="text-xs uppercase tracking-widest">Status</Label>
                <Select value={selectedApp.status} onValueChange={(v) => handleStatusChange(selectedApp.appointment_id, v)}>
                  <SelectTrigger data-testid="appointment-details-status" style={{ color: STATUS_LABELS[selectedApp.status]?.color }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([key, val]) => (
                      <SelectItem key={key} value={key} data-testid={`appointment-details-status-option-${key}`}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={() => handleDelete(selectedApp.appointment_id)} data-testid="appointment-details-delete">
                  <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Excluir
                </Button>
                <Button variant="outline" onClick={() => setDetailsOpen(false)}>Fechar</Button>
                {selectedApp.status !== "concluido" && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const d = new Date(selectedApp.date);
                        const pad = (n) => String(n).padStart(2, "0");
                        setRescheduleForm({
                          date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
                          time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
                          employee_id: selectedApp.employee_id || "",
                          notes: "",
                        });
                        setDetailsOpen(false);
                        setRescheduleOpen(true);
                      }}
                      data-testid="appointment-details-reschedule"
                    >
                      <Clock className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Reagendar
                    </Button>
                    <Button
                      onClick={() => {
                        setDetailsOpen(false);
                        openCompleteFlow(selectedApp);
                      }}
                      data-testid="appointment-details-finalize"
                      style={{ background: "#166534" }}
                      className="text-white hover:opacity-90"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Finalizar com Pagamento
                    </Button>
                  </>
                )}
                {selectedApp.status === "concluido" && (
                  <Button
                    onClick={() => {
                      setDetailsOpen(false);
                      window.location.assign(`/comandas?focus=${selectedApp.appointment_id}`);
                    }}
                    data-testid="appointment-details-view-comanda"
                    style={{ background: "#166534" }}
                    className="text-white hover:opacity-90"
                  >
                    <FileText className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Ver comanda · Reabrir
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete with Payment Dialog */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden p-5 bg-background">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-light tracking-tight text-xl flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} style={{ color: "#166534" }} />
              Finalizar Atendimento
            </DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <form onSubmit={handleCompleteSubmit} className="space-y-3 mt-2 w-full overflow-x-hidden">
              {/* TOTAL PREVISTO + OVERRIDE com justificativa */}
              <div className="p-2.5 bg-secondary/50 rounded-md text-sm">
                <div className="font-medium">{selectedApp.client_name}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {selectedApp.services?.map((s) => s.service_name).join(", ")}
                </div>
                {selectedApp.employee_name && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Profissional: {selectedApp.employee_name}
                  </div>
                )}
                <div className="flex justify-between items-center text-sm pt-2 mt-2 border-t border-border">
                  <span className="text-muted-foreground text-xs">Total previsto dos serviços</span>
                  <span className="font-medium" style={{ color: "#CBA871" }}>
                    {formatCurrency(selectedApp.total_price)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={priceOverrideEnabled}
                      onChange={(e) => {
                        setPriceOverrideEnabled(e.target.checked);
                        if (!e.target.checked) {
                          setPriceOverride("");
                          setPriceOverrideReason("");
                        } else {
                          setPriceOverride((selectedApp.total_price || 0).toFixed(2));
                        }
                      }}
                      data-testid="price-override-toggle"
                    />
                    Alterar valor cobrado
                  </label>
                  {priceOverrideEnabled && (
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={priceOverride}
                      onChange={(e) => {
                        setPriceOverride(e.target.value);
                        // Auto-update first split amount
                        const newTotal = parseFloat(e.target.value) || 0;
                        if (paymentSplits.length === 1) {
                          setPaymentSplits([{ ...paymentSplits[0], amount: newTotal.toFixed(2) }]);
                        }
                      }}
                      className="h-8 w-28"
                      placeholder="Novo valor"
                      data-testid="price-override-input"
                    />
                  )}
                </div>
                {priceOverrideEnabled && (
                  <Input
                    value={priceOverrideReason}
                    onChange={(e) => setPriceOverrideReason(e.target.value)}
                    placeholder="JUSTIFICATIVA obrigatória (ex: usou mais coloração, desconto fidelidade…)"
                    className="mt-2 h-8"
                    data-testid="price-override-reason"
                    required
                  />
                )}
              </div>

              {/* SAME-DAY SIBLINGS — combinar comandas do mesmo cliente */}
              {sameDaySiblings.length > 0 && (
                <div className="p-3 border border-sky-200 bg-sky-50/60 rounded-md space-y-2" data-testid="combine-section">
                  <div className="text-xs font-medium text-sky-900">
                    {sameDaySiblings.length} outra(s) comanda(s) deste cliente HOJE — combinar?
                  </div>
                  {sameDaySiblings.map((s) => (
                    <label key={s.appointment_id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`combine-option-${s.appointment_id}`}>
                      <input
                        type="checkbox"
                        checked={combineIds.includes(s.appointment_id)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...combineIds, s.appointment_id]
                            : combineIds.filter((x) => x !== s.appointment_id);
                          setCombineIds(next);
                          // Auto-recalc default split amount
                          const newTotal = parseFloat(selectedApp?.total_price || 0)
                            + sameDaySiblings
                                .filter((x) => next.includes(x.appointment_id))
                                .reduce((a, b) => a + parseFloat(b.total_price || 0), 0);
                          setPaymentSplits([{ payment_method: paymentSplits[0]?.payment_method || "dinheiro", amount: newTotal.toFixed(2) }]);
                        }}
                      />
                      <span className="flex-1">
                        <strong>{s.employee_name || "—"}</strong> · {(s.services || []).join(", ")}
                        <span className="text-muted-foreground ml-1">
                          ({new Date(s.date).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })})
                        </span>
                      </span>
                      <span className="font-medium">{formatCurrency(s.total_price)}</span>
                    </label>
                  ))}
                  {combineIds.length > 0 && (
                    <div className="flex justify-between text-sm pt-2 mt-2 border-t border-sky-200 font-medium text-sky-900" data-testid="combined-total">
                      <span>Total combinado</span>
                      <span>{formatCurrency(computeCombinedTotal())}</span>
                    </div>
                  )}
                </div>
              )}

              {/* EXTRAS — serviços/produtos adicionados no caixa */}
              <div className="p-3 border border-amber-200 bg-amber-50/40 rounded-md space-y-2" data-testid="extras-section">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-amber-900">
                    Adicionar serviço ou produto na comanda
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setExtraItems([...extraItems, {
                        type: "servico",
                        name: "",
                        unit_price: "",
                        quantity: 1,
                        item_id: null,
                        employee_id: selectedApp?.employee_id || "",
                        employee_name: selectedApp?.employee_name || "",
                        commission_rate: null,
                      }])}
                      className="text-[10px] underline text-amber-900"
                      data-testid="add-extra-service"
                    >
                      + Serviço
                    </button>
                    <span className="text-amber-900">·</span>
                    <button
                      type="button"
                      onClick={() => setExtraItems([...extraItems, {
                        type: "produto",
                        name: "",
                        unit_price: "",
                        quantity: 1,
                        item_id: null,
                        employee_id: selectedApp?.employee_id || "",
                        employee_name: selectedApp?.employee_name || "",
                        commission_rate: null,
                      }])}
                      className="text-[10px] underline text-amber-900"
                      data-testid="add-extra-product"
                    >
                      + Produto
                    </button>
                  </div>
                </div>
                {extraItems.map((it, idx) => {
                  const isProd = it.type === "produto";
                  const catalog = isProd ? products : services;
                  const selectedItem = catalog.find((c) => (isProd ? c.product_id : c.service_id) === it.item_id);
                  const stockNow = isProd && selectedItem ? Number(selectedItem.stock_quantity ?? 0) : null;
                  const qtyWanted = parseFloat(it.quantity) || 0;
                  const stockShort = isProd && selectedItem && stockNow - qtyWanted < 0;
                  // Filter catalog by typed name (case-insensitive) when no item selected yet
                  const q = (it.name || "").trim().toLowerCase();
                  const matches = !it.item_id && q.length >= 1
                    ? catalog
                        .filter((c) => (c.name || "").toLowerCase().includes(q))
                        .slice(0, 8)
                    : [];
                  return (
                    <div key={idx} className="space-y-1" data-testid={`extra-row-${idx}`}>
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="inline-block w-[60px] text-[10px] uppercase tracking-wider text-amber-900 flex-shrink-0">
                          {isProd ? "Produto" : "Serviço"}
                        </span>
                        <div className="flex-1 min-w-0 relative">
                          <Input
                            value={it.name || ""}
                            onChange={(e) => {
                              const next = [...extraItems];
                              next[idx] = { ...next[idx], name: e.target.value, item_id: null };
                              setExtraItems(next);
                            }}
                            placeholder={isProd ? "Digite o produto…" : "Digite o serviço…"}
                            className="h-7 text-[11px]"
                            data-testid={`extra-search-${idx}`}
                            autoComplete="off"
                          />
                          {matches.length > 0 && (
                            <div className="absolute z-30 left-0 right-0 mt-0.5 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                              {matches.map((c) => {
                                const cid = isProd ? c.product_id : c.service_id;
                                return (
                                  <button
                                    key={cid}
                                    type="button"
                                    onClick={() => {
                                      const next = [...extraItems];
                                      next[idx] = {
                                        ...next[idx],
                                        item_id: cid,
                                        name: c.name,
                                        unit_price: c.price ?? 0,
                                        commission_rate: c.commission_rate != null ? c.commission_rate : next[idx].commission_rate,
                                      };
                                      setExtraItems(next);
                                    }}
                                    className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-secondary"
                                    data-testid={`extra-match-${idx}-${cid}`}
                                  >
                                    {c.name}
                                    <span className="text-muted-foreground"> · R$ {Number(c.price ?? 0).toFixed(2)}</span>
                                    {isProd && (
                                      <span className="text-muted-foreground"> · est {c.stock_quantity ?? 0}</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          value={it.quantity}
                          onChange={(e) => {
                            const next = [...extraItems];
                            next[idx] = { ...next[idx], quantity: e.target.value };
                            setExtraItems(next);
                          }}
                          className="h-7 w-12 text-[11px]"
                          title="Qtd"
                          data-testid={`extra-qty-${idx}`}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={it.unit_price}
                          onChange={(e) => {
                            const next = [...extraItems];
                            next[idx] = { ...next[idx], unit_price: e.target.value };
                            setExtraItems(next);
                          }}
                          className="h-7 w-20 text-[11px]"
                          placeholder="Unit."
                          data-testid={`extra-price-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => setExtraItems(extraItems.filter((_, i) => i !== idx))}
                          className="text-destructive px-1 flex-shrink-0"
                          data-testid={`extra-remove-${idx}`}
                          title="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                      {/* Funcionário executante / vendedor */}
                      <div className="flex items-center gap-1.5 text-xs pl-[68px]">
                        <span className="text-[10px] text-amber-900/70 flex-shrink-0">
                          {isProd ? "Vendido por" : "Profissional"}
                        </span>
                        <select
                          value={it.employee_id || ""}
                          onChange={(e) => {
                            const eid = e.target.value;
                            const emp = employees.find((x) => x.employee_id === eid);
                            const next = [...extraItems];
                            next[idx] = {
                              ...next[idx],
                              employee_id: eid || null,
                              employee_name: emp ? emp.name : "",
                            };
                            setExtraItems(next);
                          }}
                          className="h-6 flex-1 min-w-0 text-[10px] border border-input rounded-md bg-background px-1"
                          data-testid={`extra-employee-${idx}`}
                        >
                          <option value="">— sem comissão —</option>
                          {employees.map((emp) => (
                            <option key={emp.employee_id} value={emp.employee_id}>
                              {emp.name}
                            </option>
                          ))}
                        </select>
                        {it.commission_rate != null && it.commission_rate > 0 && it.employee_id && (
                          <span className="text-[9px] text-emerald-700 whitespace-nowrap flex-shrink-0">
                            {Number(it.commission_rate).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {stockShort && (
                        <div className="pl-[68px] text-[10px] text-orange-700 leading-snug" data-testid={`extra-stock-warn-${idx}`}>
                          Estoque: {stockNow}. Venda excederá em {(qtyWanted - stockNow)}.
                        </div>
                      )}
                    </div>
                  );
                })}
                {extraItems.length > 0 && (
                  <div className="flex justify-between text-xs pt-1.5 mt-1 border-t border-amber-200 font-medium text-amber-900">
                    <span>Subtotal extras</span>
                    <span data-testid="extras-subtotal">
                      {formatCurrency(extraItems.reduce((a, b) => a + (parseFloat(b.unit_price) || 0) * (parseFloat(b.quantity) || 0), 0))}
                    </span>
                  </div>
                )}
              </div>

              {/* SPLITS — múltiplas formas de pagamento */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest">Formas de Pagamento</Label>
                  <button
                    type="button"
                    onClick={() => {
                      const total = computeCombinedTotal() + (parseFloat(creditDeposit) || 0);
                      const already = paymentSplits.reduce((a, s) => a + (parseFloat(s.amount) || 0), 0);
                      const remaining = Math.max(0, total - already);
                      setPaymentSplits([...paymentSplits, { payment_method: "dinheiro", amount: remaining > 0 ? remaining.toFixed(2) : "" }]);
                    }}
                    className="text-xs underline text-primary"
                    data-testid="complete-add-split"
                  >
                    + Adicionar forma
                  </button>
                </div>
                {paymentSplits.map((sp, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`split-row-${idx}`}>
                    <Select
                      value={sp.payment_method}
                      onValueChange={(v) => {
                        const next = [...paymentSplits];
                        next[idx] = { ...next[idx], payment_method: v };
                        setPaymentSplits(next);
                      }}
                    >
                      <SelectTrigger className="h-9" data-testid={`split-method-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dinheiro">Dinheiro</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="credito">Crédito</SelectItem>
                        <SelectItem value="debito">Débito</SelectItem>
                        <SelectItem value="transferencia">Transferência</SelectItem>
                        <SelectItem value="boleto">Boleto</SelectItem>
                        <SelectItem value="fiado">Fiado (gera cobrança)</SelectItem>
                        <SelectItem value="credito_cliente" disabled={clientCreditBalance <= 0}>
                          Crédito do cliente {clientCreditBalance > 0 ? `(R$ ${clientCreditBalance.toFixed(2)} disp.)` : "(sem saldo)"}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      value={sp.amount}
                      onChange={(e) => {
                        const next = [...paymentSplits];
                        next[idx] = { ...next[idx], amount: e.target.value };
                        setPaymentSplits(next);
                      }}
                      className="h-9 w-28"
                      data-testid={`split-amount-${idx}`}
                    />
                    {paymentSplits.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setPaymentSplits(paymentSplits.filter((_, i) => i !== idx))}
                        className="text-destructive hover:opacity-70 px-2"
                        data-testid={`split-remove-${idx}`}
                        aria-label="Remover"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                ))}

                {/* Deixar como crédito */}
                <div className="flex items-center gap-2 pt-1">
                  <Label className="text-xs uppercase tracking-widest flex-1">
                    Deixar como crédito do cliente
                    <span className="block text-[10px] text-muted-foreground normal-case tracking-normal">
                      (Cliente paga mais e deixa saldo para próxima visita)
                    </span>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    value={creditDeposit}
                    onChange={(e) => setCreditDeposit(e.target.value)}
                    className="h-9 w-28"
                    data-testid="credit-deposit-input"
                  />
                </div>

                {/* Live reconciliation */}
                {(() => {
                  const total = computeCombinedTotal();
                  const dep = parseFloat(creditDeposit) || 0;
                  const expected = total + dep;
                  const sum = paymentSplits.reduce((a, b) => a + (parseFloat(b.amount) || 0), 0);
                  const diff = expected - sum;
                  const ok = Math.abs(diff) <= 0.02;
                  return (
                    <div
                      className={`p-2.5 rounded-md text-xs flex justify-between items-center border ${ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
                      data-testid="split-reconciliation"
                    >
                      <span>
                        Pago: <strong>{formatCurrency(sum)}</strong> · Comanda: <strong>{formatCurrency(total)}</strong>
                        {dep > 0 && <> · Crédito: <strong>{formatCurrency(dep)}</strong></>}
                      </span>
                      {ok ? (
                        <span className="uppercase tracking-wider font-medium">✓ Fechado</span>
                      ) : diff > 0 ? (
                        <span className="font-medium">Faltam {formatCurrency(diff)}</span>
                      ) : (
                        <span className="font-medium">Excede {formatCurrency(-diff)} → deixe como crédito</span>
                      )}
                    </div>
                  );
                })()}

                {paymentSplits.some((s) => s.payment_method === "fiado") && (
                  <div className="p-2 text-[11px] rounded-md bg-amber-50 border border-amber-200 text-amber-800">
                    <strong>⚠️ Fiado:</strong> uma cobrança será criada em "Cobranças" com vencimento em 30 dias.
                  </div>
                )}
                {paymentSplits.some((s) => s.payment_method === "credito_cliente") && (
                  <div className="p-2 text-[11px] rounded-md bg-sky-50 border border-sky-200 text-sky-800">
                    Será debitado do crédito do cliente (saldo atual: R$ {clientCreditBalance.toFixed(2)}).
                  </div>
                )}
                {parseFloat(creditDeposit) > 0 && (
                  <div className="p-2 text-[11px] rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800">
                    <strong>💰 Crédito:</strong> R$ {parseFloat(creditDeposit).toFixed(2)} ficará no saldo do cliente para próxima visita.
                  </div>
                )}
              </div>

              {/* Gorjeta (opcional) */}
              <div>
                <Label className="text-xs uppercase tracking-widest">Gorjeta (opcional)</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[
                    { pct: 0, label: "Sem" },
                    { pct: 5, label: "5%" },
                    { pct: 10, label: "10%" },
                    { pct: 15, label: "15%" },
                    { pct: 20, label: "20%" },
                  ].map((opt) => (
                    <button
                      key={opt.pct}
                      type="button"
                      onClick={() => {
                        const baseAmount = selectedApp?.total_price || 0;
                        const tip = (baseAmount * opt.pct) / 100;
                        setCompleteForm({ ...completeForm, tip_amount: tip.toFixed(2) });
                      }}
                      className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-md border border-border hover:bg-secondary transition-colors font-medium"
                      data-testid={`tip-suggestion-${opt.pct}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={completeForm.tip_amount}
                  onChange={(e) => setCompleteForm({ ...completeForm, tip_amount: e.target.value })}
                  placeholder="0,00"
                  data-testid="complete-tip-input"
                />
                {parseFloat(completeForm.tip_amount) > 0 && selectedApp?.employee_name && (
                  <p className="text-xs mt-1" style={{ color: "#166534" }}>
                    Gorjeta de {formatCurrency(parseFloat(completeForm.tip_amount))} registrada para {selectedApp.employee_name}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest">Observações</Label>
                <Input
                  value={completeForm.notes}
                  onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })}
                  placeholder="Ex: Desconto fidelidade, gorjeta..."
                  data-testid="complete-notes-input"
                />
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setCompleteOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="complete-submit-button"
                  style={{ background: "#166534" }}
                  className="text-white hover:opacity-90"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Finalizar e Registrar Pagamento
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog — preserves history (no exclude+create) */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-xl">Reagendar</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <form onSubmit={handleRescheduleSubmit} className="space-y-3 mt-2">
              <div className="p-3 bg-secondary/40 rounded-md text-xs">
                <div className="font-medium text-sm">{selectedApp.client_name}</div>
                <div className="text-muted-foreground mt-1">
                  Original: {new Date(selectedApp.date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                  {selectedApp.employee_name && ` · ${selectedApp.employee_name}`}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Nova data</Label>
                  <Input
                    type="date"
                    value={rescheduleForm.date}
                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, date: e.target.value })}
                    required
                    data-testid="reschedule-date"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Novo horário</Label>
                  <Input
                    type="time"
                    value={rescheduleForm.time}
                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, time: e.target.value })}
                    required
                    data-testid="reschedule-time"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Funcionário (opcional)</Label>
                <Select
                  value={rescheduleForm.employee_id || "_keep"}
                  onValueChange={(v) => setRescheduleForm({ ...rescheduleForm, employee_id: v === "_keep" ? "" : v })}
                >
                  <SelectTrigger data-testid="reschedule-employee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_keep">Manter atual</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Motivo (opcional)</Label>
                <Input
                  value={rescheduleForm.notes}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, notes: e.target.value })}
                  placeholder="Ex: cliente pediu para mudar de horário"
                  data-testid="reschedule-notes"
                />
              </div>
              <p className="text-[10px] text-muted-foreground">
                Esta ação será registrada no histórico do agendamento (quem alterou e quando), preservando rastreabilidade.
              </p>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setRescheduleOpen(false)} data-testid="reschedule-cancel">
                  Cancelar
                </Button>
                <Button type="submit" data-testid="reschedule-submit">
                  Confirmar Reagendamento
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick "New Client" Dialog — accessible from the appointment form */}
      <Dialog open={newClientOpen} onOpenChange={setNewClientOpen}>        <DialogContent className="sm:max-w-md" data-testid="quick-new-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Novo Cliente</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateNewClient} className="space-y-3 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nome *</Label>
              <Input
                autoFocus
                value={newClientForm.name}
                onChange={(e) => setNewClientForm({ ...newClientForm, name: e.target.value })}
                placeholder="Nome completo"
                data-testid="quick-new-client-name"
                required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Telefone</Label>
              <Input
                value={newClientForm.phone}
                onChange={(e) => setNewClientForm({ ...newClientForm, phone: e.target.value })}
                placeholder="(11) 99999-9999"
                data-testid="quick-new-client-phone"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email</Label>
              <Input
                type="email"
                value={newClientForm.email}
                onChange={(e) => setNewClientForm({ ...newClientForm, email: e.target.value })}
                placeholder="email@exemplo.com"
                data-testid="quick-new-client-email"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setNewClientOpen(false)} data-testid="quick-new-client-cancel">
                Cancelar
              </Button>
              <Button type="submit" data-testid="quick-new-client-submit">
                Criar e selecionar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
